import axios from 'axios';

/**
 * Resolve API base URL.
 * On live domain always use same-origin /api — never trust a baked localhost URL
 * from a misconfigured CRA env during build.
 */
function resolveApiBase() {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host && host !== 'localhost' && host !== '127.0.0.1') {
      return '/api';
    }
  }

  const env = String(process.env.REACT_APP_BACKEND_URL || '').trim();
  if (env && !/127\.0\.0\.1|localhost/i.test(env)) {
    return `${env.replace(/\/$/, '')}/api`;
  }

  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }

  return 'http://127.0.0.1:8000/api';
}

const API = resolveApiBase();

const client = axios.create({
  baseURL: API,
  timeout: 90000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

client.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('belbel_token');
  if (token) {
    // Authorization sering di-strip Apache/cPanel → kirim juga header kustom
    cfg.headers.Authorization = `Bearer ${token}`;
    cfg.headers['X-Belbel-Token'] = `Bearer ${token}`;
  }
  return cfg;
});

client.interceptors.response.use(
  (r) => {
    const ct = String(r.headers?.['content-type'] || '');
    const url = String(r.config?.url || '');
    // API tidak boleh mengembalikan HTML (tanda jatuh ke SPA / error document)
    if (url.includes('/admin/') || url.includes('/auth/') || url.includes('/settings')) {
      if (ct.includes('text/html') || (typeof r.data === 'string' && /^\s*<!doctype/i.test(r.data))) {
        return Promise.reject({
          response: {
            status: r.status || 502,
            data: {
              detail: 'API mengembalikan HTML. Cek .htaccess / login ulang.',
            },
          },
          config: r.config,
          message: 'API returned HTML',
        });
      }
    }
    return r;
  },
  (err) => {
    if (err.response && err.response.status === 401) {
      const url = err.config?.url || '';
      if (url.includes('/admin/') || url.includes('/auth/me')) {
        localStorage.removeItem('belbel_token');
        localStorage.removeItem('belbel_user');
      }
    }
    return Promise.reject(err);
  }
);

/** Ambil pesan error yang bisa dibaca dari respons API Laravel/axios */
export function apiErrorMessage(err, fallback = 'Terjadi kesalahan. Coba lagi.') {
  const data = err?.response?.data;
  const asText = (v) => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  };

  const looksLikeHtml = (s) => {
    const t = String(s || '').trim().toLowerCase();
    return t.startsWith('<!doctype') || t.startsWith('<html') || t.includes('<meta charset');
  };

  if (data) {
    if (typeof data.detail === 'string' && data.detail.trim() && !looksLikeHtml(data.detail)) {
      return data.detail;
    }
    if (typeof data.message === 'string' && data.message.trim() && data.message !== 'Server Error' && !looksLikeHtml(data.message)) {
      return data.message;
    }
    if (data.errors && typeof data.errors === 'object') {
      const first = Object.values(data.errors).flat()[0];
      if (first) return String(first);
    }
    const raw = asText(data);
    if (looksLikeHtml(raw)) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) return 'Sesi habis atau tidak diizinkan. Login ulang.';
      return 'Server mengembalikan halaman HTML, bukan API. Pastikan .htaccess benar, lalu hard refresh & login ulang.';
    }
    if (typeof data === 'string' && data.trim()) return data.slice(0, 180);
  }
  if (err?.code === 'ECONNABORTED') return 'Timeout — file terlalu besar atau server lambat.';
  if (!err?.response) return 'Tidak terhubung ke server. Cek koneksi / login ulang.';
  if (err.response.status === 413) return 'File terlalu besar untuk server (413). Perkecil gambar.';
  if (err.response.status === 401) return 'Sesi habis. Silakan login ulang.';
  if (err.response.status === 419) return 'Sesi formulir kadaluarsa. Refresh lalu coba lagi.';
  if (err.response.status >= 500) return 'Server error saat menyimpan. Coba lagi atau login ulang.';
  return fallback;
}

export function getSessionId() {
  let sid = localStorage.getItem('belbel_sid');
  if (!sid) {
    sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('belbel_sid', sid);
  }
  return sid;
}

/** Normalize logo/media path for <img src> */
export function mediaUrl(path) {
  if (!path) return '';
  const s = String(path).trim();
  if (!s) return '';
  if (s.startsWith('data:') || s.startsWith('http://') || s.startsWith('https://') || s.startsWith('blob:')) {
    return s;
  }
  if (s.startsWith('//')) {
    return `${typeof window !== 'undefined' ? window.location.protocol : 'https:'}${s}`;
  }
  return s.startsWith('/') ? s : `/${s}`;
}

export default client;
